import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useGetLocationListQuery } from '../../../../../api/locations/locationsApiSlice';
import { useFetchApartments } from '../../../../../flows/fetchApartmentsFlow';
import InputField from '../../../../../components/ui/InputField';
import BookingCountryField from '../../../../../components/booking/BookingCountryField';
import SelectField from '../../../../../components/ui/SelectField';
import EventTypeField from '../EventTypeField';
import BookingDatePicker from '../../../../common/BookingDatePicker';
import { getAvailableBookingTimes } from '../../../../../utils/dateTimeUtils';
import { useLocationTimeZone } from '../../../../../hooks/useLocationTimeZone';
import { formatCurrency, sortByLocaleName } from '../../../../../utils';
import { useEventTypeOptions } from '../../../../../hooks/useEventTypeOptions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import { setSelectedApartment, setSelectedApartmentDetails } from '../../../../../store/slices/bookingCreationSlice';
import { useFetchApartmentDetails } from '../../../../../flows/fetchApartmentDetailsFlow';
import { AdditionalService } from '../../../../../types/bookingCreation';
import { getCurrentLang } from '../../../../../i18n/i18n';
import BookingAttributionFields, { BookingAttribution } from '../../../../../components/booking/BookingAttributionFields';
import { spaceOf } from '../../../../../utils/useCases';
import type { UseCase } from '../../../../../utils/useCases';
import { roomNounOf, serviceTypeLabel } from '../../../../../utils/useCaseLabels';
import { seriesToggleUseCases } from './seriesToggleUseCases';

export interface SelectedAdditionalService {
    serviceId: string;
    name: string;
    quantity: number;
    unitPrice: number;
}

export interface SeriesBookingData {
    assetId: string;
    /**
     * Which OFFER every occurrence is booked under. Carried separately from
     * `assetId` because a divided room's object belongs to a SEGMENT, so the
     * backend's fallback to `asset.group_id` books the part (#2650).
     */
    groupId: string;
    assetName: string;
    locationId: string;
    locationName: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company: string;
    address: string;
    country: string;
    customerCity: string;
    zipCode: string;
    startTime: string;
    endTime: string;
    startDate: Dayjs | null;
    numberOfGuests: number;
    eventType: string;
    comment: string;
    /** A live room use case: venue, apartment, or hall (since W1-1). */
    useCase: UseCase;
    city: string;
    additionalServices: SelectedAdditionalService[];
    /** #1810: organisation (unit) and cost centre, the same for every occurrence. */
    attribution: BookingAttribution;
}

interface BookingDataFormProps {
    data: SeriesBookingData;
    /**
     * The page's state setter. Takes an updater, so two updates in one event —
     * picking an organisation sets its prefill AND the attribution — both
     * land; spreading `data` twice would keep only the last.
     */
    onChange: React.Dispatch<React.SetStateAction<SeriesBookingData>>;
}

const BookingDataForm: React.FC<BookingDataFormProps> = ({ data, onChange }) => {
    const { t, i18n } = useTranslation();
    const timeZone = useLocationTimeZone(data.locationId);
    const dispatch = useDispatch();
    const currentLanguage = i18n.language;
    const fetchApartmentDetails = useFetchApartmentDetails();
    // Configured-else-standard venue vocabulary — see useEventTypeOptions.
    const eventTypeOptions = useEventTypeOptions(data.eventType, data.useCase);

    // Use case selection
    const [useCase, setUseCase] = useState<UseCase>(data.useCase);
    const noun = roomNounOf(useCase);
    // The room use cases the toggle offers: the two it always offered, plus
    // any other the tenant has switched on (a sports hall).
    const tenantServices = useSelector((state: RootState) => state.authentication.config?.services);
    const toggleUseCases = useMemo(
        () => seriesToggleUseCases(tenantServices ?? []),
        [tenantServices],
    );

    // Location data
    const { data: locations, isLoading: loadingLocations } = useGetLocationListQuery(useCase);
    const [cities, setCities] = useState<string[]>([]);
    const [loadingApartments, setLoadingApartments] = useState(false);

    // Asset options (venues/apartments)
    const [assetOptions, setAssetOptions] = useState<{ id: string, name: string, assetId: string, groupId: string, locationId: string }[]>([]);

    // Apartments from Redux (populated by fetchApartments flow)
    const apartments = useSelector(
        (state: RootState) => state.bookingCreation.apartments,
    );

    // Apartment details (includes additional services)
    const selectedApartmentDetails = useSelector(
        (state: RootState) => state.bookingCreation.selectedApartmentDetails,
    );

    const availableAdditionalServices: AdditionalService[] =
        selectedApartmentDetails?.assetAdditionalServices ?? [];

    // Extract cities from locations
    useEffect(() => {
        if (locations && locations.length > 0) {
            setCities(
                Array.from(
                    new Set(locations.map((loc: any) => loc.address.city)),
                ),
            );
        }
    }, [locations]);

    // Fetch ALL assets for this city (no availability filtering).
    // For series bookings, conflict detection happens per date in Step 3.
    const fetchCheckIn = useMemo(() => dayjs('2099-12-31').hour(0).minute(0).second(0), []);
    const fetchCheckOut = useMemo(() => dayjs('2099-12-31').hour(0).minute(1).second(0), []);

    const fetchApartments = useFetchApartments(
        fetchCheckIn,
        fetchCheckOut,
        data.city,
        1,
        undefined,
        useCase,
    );

    useEffect(() => {
        const loadAssets = async () => {
            if (data.city && fetchApartments) {
                setLoadingApartments(true);
                await fetchApartments({ includeCurrentApartment: true });
                setLoadingApartments(false);
            }
        };
        loadAssets();
    }, [data.city, useCase]);

    // Transform apartments to options
    useEffect(() => {
        if (apartments) {
            setAssetOptions(sortByLocaleName(apartments.map((apart: any) => ({
                id: apart.id,
                name: apart?.langNames?.[currentLanguage]?.title || apart?.name,
                assetId: apart.assetId || apart.id,
                groupId: apart.groupId || apart.id,
                locationId: apart.locationId || '',
            })), (option) => option.name));
        }
    }, [apartments, currentLanguage]);

    // Fetch apartment details when asset is selected (for additional services)
    useEffect(() => {
        const loadDetails = async () => {
            dispatch(setSelectedApartmentDetails(null));
            if (data.assetId) {
                await fetchApartmentDetails();
            }
        };
        loadDetails();
    }, [data.assetId]);

    // Generate available dates (today → 18 months)
    const availableDates = useMemo(() => {
        const dates: string[] = [];
        const start = dayjs().startOf('day');
        const end = dayjs().add(18, 'month').endOf('month');
        for (let date = start; date.isBefore(end); date = date.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }
        return dates;
    }, []);

    // Generate time slots — "today" and its past hours on the clock of the
    // venue's location (#3219).
    let availableStartTimes: string[] = [];
    let availableEndTimes: string[] = [];
    if (data.startDate) {
        const result = getAvailableBookingTimes(data.startTime, data.startDate, undefined, timeZone);
        availableStartTimes = result.availableStartTimes;
        availableEndTimes = result.availableEndTimes;
    }

    const update = (partial: Partial<SeriesBookingData>) => {
        onChange((current) => ({ ...current, ...partial }));
    };

    // Auto-select city when only one is available
    useEffect(() => {
        if (cities.length === 1 && !data.city) {
            update({ city: cities[0] });
        }
    }, [cities, data.city]);

    return (
        <div className={'rounded-lg bg-white p-6 shadow-sm'}>
            {/* Service toggle */}
            <div className={'mb-6 flex gap-2'}>
                {toggleUseCases.map((option) => (
                    <button
                        key={option}
                        onClick={() => { setUseCase(option); update({ useCase: option }); }}
                        className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                            useCase === option
                                ? 'bg-primary text-text-buttonContrast'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {serviceTypeLabel(t, option)}
                    </button>
                ))}
            </div>

            <Grid container spacing={3}>
                {/* City */}
                <Grid item xs={12} md={6}>
                    <SelectField
                        value={data.city ?? ''}
                        onChange={(city: string) => update({ city, assetId: '', groupId: '', assetName: '' })}
                        options={cities}
                        disabled={cities.length === 1}
                        label={t('create_booking.reservation_details.city') + ' *'}
                        loading={loadingLocations}
                    />
                </Grid>

                {/* Start date (first occurrence) */}
                <Grid item xs={12} md={6}>
                    <BookingDatePicker
                        value={data.startDate}
                        onChange={(date: Dayjs | null) => update({ startDate: date?.startOf('day') ?? null })}
                        availableDates={availableDates}
                        label={t('series_booking.booking_data.start_date_label') + ' *'}
                    />
                </Grid>

                {/* Start time */}
                <Grid item xs={12} md={6}>
                    <SelectField
                        options={availableStartTimes}
                        value={data.startTime ?? ''}
                        onChange={(time: string) => update({ startTime: time })}
                        label={t('create_booking.reservation_details.start_time') + ' *'}
                    />
                </Grid>

                {/* End time */}
                <Grid item xs={12} md={6}>
                    <SelectField
                        options={availableEndTimes}
                        value={data.endTime ?? ''}
                        onChange={(time: string) => update({ endTime: time })}
                        label={t('create_booking.reservation_details.end_time') + ' *'}
                    />
                </Grid>

                {/* Number of Guests — the initial 0 renders as empty. The field
                    is required and min={1} since #1749 made the count enforced,
                    so showing "0" would give the wizard a filled-looking field
                    with Next disabled and nothing saying why. */}
                <Grid item xs={12} md={6}>
                    <InputField
                        type={'number'}
                        min={1}
                        integerOnly
                        onChange={(value) => update({ numberOfGuests: Number(value) })}
                        value={data.numberOfGuests ? String(data.numberOfGuests) : ''}
                        label={t('create_booking.reservation_details.number_of_guests') + ' *'}
                    />
                </Grid>

                {/* Asset/Venue Selection */}
                <Grid item xs={12} md={6}>
                    <SelectField
                        value={data.assetName ?? ''}
                        onChange={(name: string) => {
                            const selected = assetOptions.find((a) => a.name === name);
                            if (selected) {
                                // Set in local state
                                update({
                                    assetId: selected.assetId,
                                    groupId: selected.groupId,
                                    assetName: selected.name,
                                    locationId: selected.locationId,
                                    additionalServices: [],
                                });
                                // Set in Redux to trigger apartment details fetch
                                const apartment = apartments?.find((a: any) => a.id === selected.id);
                                if (apartment) {
                                    dispatch(setSelectedApartment(apartment));
                                }
                            }
                        }}
                        options={assetOptions.map((a) => a.name)}
                        disabled={!data.city || !data.startDate || !data.startTime || !data.endTime}
                        label={t(`create_booking.reservation_details.${noun}`) + ' *'}
                        loading={loadingApartments}
                        noOptions={t(`create_booking.reservation_details.no_${noun}s_match`)}
                    />
                </Grid>

                {/* Event type (use cases that ask for the event) */}
                {spaceOf(useCase)?.eventTypes === true && (
                    <Grid item xs={12} md={6}>
                        <EventTypeField
                            groupId={data.groupId}
                            options={eventTypeOptions}
                            value={data.eventType}
                            onChange={(val: string) => update({ eventType: val })}
                        />
                    </Grid>
                )}
            </Grid>

            {/* Additional services */}
            <Typography variant={'h6'} sx={{ mt: 4, mb: 2 }}>
                {t('create_booking.additional_services.title')}
            </Typography>
            {data.assetId ? (
                availableAdditionalServices.length > 0 ? (
                    <div className={'space-y-2'}>
                        {availableAdditionalServices
                            .filter((s) => !s.isPaymentCompulsory)
                            .map((service) => {
                                const isSelected = data.additionalServices.some(
                                    (s) => s.serviceId === service.id,
                                );
                                const selectedService = data.additionalServices.find(
                                    (s) => s.serviceId === service.id,
                                );
                                const serviceName = service.langNames?.[getCurrentLang()] || service.serviceName;
                                return (
                                    <div key={service.id} className={'flex items-center gap-3'}>
                                        <input
                                            type={'checkbox'}
                                            checked={isSelected}
                                            onChange={() => {
                                                if (isSelected) {
                                                    update({
                                                        additionalServices: data.additionalServices.filter(
                                                            (s) => s.serviceId !== service.id,
                                                        ),
                                                    });
                                                } else {
                                                    update({
                                                        additionalServices: [
                                                            ...data.additionalServices,
                                                            {
                                                                serviceId: service.id,
                                                                name: service.serviceName,
                                                                quantity: 1,
                                                                unitPrice: service.unitPrice,
                                                            },
                                                        ],
                                                    });
                                                }
                                            }}
                                            className={'h-4 w-4 rounded border-gray-300 text-primary'}
                                        />
                                        <span className={'text-sm'}>
                                            {serviceName}
                                            {/* NO unit suffix, deliberately — the same
                                                correction the end-user summary got.
                                                This appended `booking_details.per_person`
                                                to every priced add-on, and the unit it
                                                named was invented: the line total is
                                                `unitPrice × quantity`, where quantity is
                                                the stepper value (or 1). The guest count
                                                never enters it.

                                                `asset_additional_services` has
                                                `unit_price` and a boolean `quantity` and
                                                NO unit column, so per-person pricing does
                                                not exist to be chosen — this was not a
                                                tenant misconfiguration. Giving add-ons a
                                                real unit is a schema change and therefore
                                                a lead-dev hand-off (ADR-0004); until it
                                                lands, the bare price is the honest
                                                rendering. */}
                                            <span className={'ml-1 text-gray-500'}>
                                                | {service.unitPrice === 0
                                                    ? t('booking_details.free')
                                                    : formatCurrency(service.unitPrice, getCurrentLang())
                                                }
                                            </span>
                                        </span>
                                        {isSelected && service.quantity && (
                                            <div className={'flex items-center gap-1'}>
                                                <button
                                                    onClick={() => update({
                                                        additionalServices: data.additionalServices.map((s) =>
                                                            s.serviceId === service.id
                                                                ? { ...s, quantity: Math.max(1, s.quantity - 1) }
                                                                : s,
                                                        ),
                                                    })}
                                                    className={'h-6 w-6 rounded bg-gray-200 text-sm'}
                                                >
                                                    -
                                                </button>
                                                <span className={'w-6 text-center text-sm'}>
                                                    {selectedService?.quantity ?? 1}
                                                </span>
                                                <button
                                                    onClick={() => update({
                                                        additionalServices: data.additionalServices.map((s) =>
                                                            s.serviceId === service.id
                                                                ? { ...s, quantity: s.quantity + 1 }
                                                                : s,
                                                        ),
                                                    })}
                                                    className={'h-6 w-6 rounded bg-gray-200 text-sm'}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                ) : (
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {t('series_booking.booking_data.no_additional_services')}
                    </Typography>
                )
            ) : (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t(spaceOf(useCase)?.flow === 'event'
                        ? `create_booking.additional_services.note_${noun}`
                        : 'create_booking.additional_services.note')}
                </Typography>
            )}

            {/* Customer details — matches single booking form */}
            <Typography variant={'h6'} sx={{ mt: 4, mb: 2 }}>
                {t('create_booking.customer_details.title')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <InputField
                        onChange={(val) => update({ firstName: val })}
                        value={data.firstName}
                        label={t('create_booking.customer_details.first_name') + ' *'}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <InputField
                        onChange={(val) => update({ lastName: val })}
                        value={data.lastName}
                        label={t('create_booking.customer_details.last_name') + ' *'}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <InputField
                        onChange={(val) => update({ email: val })}
                        value={data.email}
                        label={t('create_booking.customer_details.email') + ' *'}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <InputField
                        onChange={(val) => update({ phone: val })}
                        value={data.phone}
                        label={t('create_booking.customer_details.phone')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <InputField
                        onChange={(val) => update({ company: val })}
                        value={data.company}
                        label={t('create_booking.customer_details.company')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <BookingAttributionFields
                        value={data.attribution}
                        onChange={(attribution) => update({ attribution })}
                        onOrganizationPicked={(prefill) => update({
                            company: prefill.company,
                            ...(prefill.address !== undefined ? { address: prefill.address } : {}),
                            ...(prefill.zipCode !== undefined ? { zipCode: prefill.zipCode } : {}),
                            ...(prefill.city !== undefined ? { customerCity: prefill.city } : {}),
                            ...(prefill.country !== undefined ? { country: prefill.country } : {}),
                        })}
                    />
                </Grid>
                <Grid item xs={12}>
                    <InputField
                        onChange={(val) => update({ address: val })}
                        value={data.address}
                        label={t('create_booking.customer_details.address')}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <InputField
                        onChange={(val) => update({ customerCity: val })}
                        value={data.customerCity}
                        label={t('create_booking.customer_details.city')}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <InputField
                        onChange={(val) => update({ zipCode: val })}
                        value={data.zipCode}
                        label={t('create_booking.customer_details.zip_code')}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <BookingCountryField
                        onChange={(val) => update({ country: val })}
                        value={data.country}
                        label={t('create_booking.customer_details.country')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <InputField
                        onChange={(val) => update({ comment: val })}
                        value={data.comment}
                        label={t('create_booking.customer_details.comment')}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default BookingDataForm;
